import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Form, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { FaPaperPlane } from 'react-icons/fa';
import { v4 } from 'uuid';

import Loading from '../../components/Loading';
import axios from '../../services/axios';
import ErrorProcessor from '../../components/ErrorProcessor';
import { useNotification } from '../../components/Notification/NotificationContext';
import Editor from '../../components/Editor';
import { formatPhoneBr } from '../../hooks/formatPhoneBr';
import SelectComponent from '../../components/SelectComponent';
import ButtonModal from '../../components/ModalConfirmation/ButtonModal';

export default function LeadNotification({ lead, stateAction, stateInfo }) {
  const [isLoading, setIsLoading] = useState(false);
  const notification = useNotification();
  const [apiErrors, setApiErrors] = useState({});
  const [leadData, setLeadData] = useState(lead);
  const [titleMsg, setTitleMsg] = useState(
    `Informações do lead ${leadData.name}`
  );
  const [destinationMsg, setDestinationMsg] = useState('');
  const [methodMsg, setMethodMsg] = useState('');
  const [filesMsg, setFilesMsg] = useState([]);
  const [bodyMsg, setBodyMsg] = useState();
  const [initialBody, setInitialBody] = useState(null);
  const [whatsappId, setWhatsappId] = useState(null);
  const [providersWhatsapp, setProvidersWhatsapp] = useState([]);

  const methodOptions = [
    { label: '-- Selecione --', value: '' },
    { label: 'E-mail', value: 'email' },
    { label: 'WhatsApp', value: 'whatsapp' },
  ];

  const [fileOptions, setFileOptions] = useState([]);

  const getLead = useCallback(async () => {
    try {
      const request = await axios.get(`leads/${leadData.id}`);
      setLeadData({
        ...request.data,
      });
    } catch (err) {
      setApiErrors(err);
    } finally {
      stateAction(false);
    }
  }, [leadData]);

  const getProvidersWhatsapp = useCallback(async () => {
    try {
      const request = await axios.get(`/whatsappproviders`);
      const providersOptions = [{ id: '', name: '-- Selecione --' }];
      request.data.forEach((provider) => {
        if (provider.company_id === leadData.company_id) {
          providersOptions.push(provider);
        }
      });
      setProvidersWhatsapp(providersOptions);
    } catch (err) {
      setApiErrors(err);
    }
  }, []);

  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      const msgErros = [];

      if (!methodMsg) {
        msgErros.push(
          <span className="text-danger fw-bold">
            Nenhum método de envio informado.
            <br />
          </span>
        );
      }

      if (!destinationMsg) {
        msgErros.push(
          <span className="text-danger fw-bold">
            Nenhum destinatário informado.
            <br />
          </span>
        );
      }

      if (!titleMsg) {
        msgErros.push(
          <span className="text-danger fw-bold">
            Título da mensagem não informado.
            <br />
          </span>
        );
      }

      if (!bodyMsg) {
        msgErros.push(
          <span className="text-danger fw-bold">
            Mensagem não informada.
            <br />
          </span>
        );
      }

      if (msgErros.length > 0) {
        notification(msgErros);
        return;
      }

      const formData = {
        destination: destinationMsg,
        type: methodMsg,
        title: titleMsg,
        body: bodyMsg,
        files: filesMsg,
        id: leadData.id,
        whatsapp_id: whatsappId,
      };

      await axios.post(`/leads/sendmessage`, formData);

      notification(`Mensagem enviada com sucesso`, 'success');
      getLead();
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  };

  const providersWpOptions = useMemo(() => {
    return providersWhatsapp.map((dataObject) => {
      return {
        value: dataObject.id,
        label: `${dataObject.name}`,
      };
    });
  }, [providersWhatsapp]);

  useEffect(() => {
    const dataBody = `
    <h1>${leadData.name}</h1>
    <br>
    <p>Documento : <b>${leadData.doc_id}</b></p>
    <p>Protocolo : <b>${leadData.protocol}</b></p>
    <p>Observações : <b>${leadData.notes}</b></p>
    `;

    let dataChannelLead = `
      <br>
      <h2>${leadData.Channel.name}</h2>
    `;

    if (leadData.LeadIvrOpenNewCanceled) {
      dataChannelLead += `
        <br>
        <p>Tipo : <b>${leadData.LeadIvrOpenNewCanceled.tipo || 'Não identificado'}</b></p>
        <p>Telefone : <b>${formatPhoneBr(leadData.LeadIvrOpenNewCanceled.phone)}</b></p>
        <p>Método de reembolso : <b>${leadData.LeadIvrOpenNewCanceled.metodo_reembolso}</b></p>
        <p>Banco : <b>${leadData.LeadIvrOpenNewCanceled.banco}</b></p>
        <p>Conta : <b>${leadData.LeadIvrOpenNewCanceled.conta}</b></p>
        <p>Titular da conta : <b>${leadData.LeadIvrOpenNewCanceled.titular_conta}</b></p>
        <p>Pix : <b>${leadData.LeadIvrOpenNewCanceled.pix}</b></p>
      `;
    }

    if (!initialBody) {
      setInitialBody(dataBody + dataChannelLead);
      setBodyMsg(dataBody + dataChannelLead);
    }

    let leadFiles = [];

    if (leadData.LeadFiles) {
      leadFiles = leadData.LeadFiles.map((file) => {
        return {
          label: file.name,
          value: file.id,
        };
      });
    }

    setFileOptions(leadFiles);
  }, [leadData, initialBody]);

  useEffect(() => {
    getProvidersWhatsapp();
  }, []);

  useEffect(() => {
    async function updateLead() {
      try {
        await getLead();
      } catch (error) {
        console.error(error);
      }
    }

    if (stateInfo === true) {
      updateLead();
    }
  }, [stateInfo]);

  return (
    <Card className="mb-5">
      <ErrorProcessor error={apiErrors} />
      <Loading isLoading={isLoading} />
      <Card.Body>
        <Col sm={12} className="d-flex flex-row-reverse mb-0">
          <ButtonModal
            onConfirm={() => handleSubmit()}
            message={`Confirma envio de mensagem do lead ${leadData.name} ?`}
            buttonProps={{
              variant: 'outline-primary',
            }}
            textModal={{
              title: `Enviar mensage do lead`,
              textConfirm: `Enviar`,
            }}
          >
            <FaPaperPlane className="me-2" /> Enviar
          </ButtonModal>
        </Col>
        <Form.Label htmlFor="method">Método de envio</Form.Label>
        <Form.Select
          id="method"
          type="select"
          className="mb-2"
          value={methodMsg}
          onChange={(e) => {
            setMethodMsg(e.target.value);
          }}
        >
          {methodOptions.map((option) => (
            <option key={v4()} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
        {methodMsg === 'whatsapp' && (
          <>
            <Form.Label htmlFor="whatsapp_id">Provedor WhatsApp</Form.Label>
            <Form.Select
              id="whatsapp_id"
              type="select"
              className="mb-2"
              value={whatsappId}
              onChange={(e) => {
                setWhatsappId(e.target.value);
              }}
            >
              {providersWpOptions.map((option) => (
                <option key={v4()} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </>
        )}
        <Form.Label htmlFor="title">Título</Form.Label>
        <Form.Control
          id="title"
          type="text"
          className="mb-2"
          value={titleMsg}
          onChange={(e) => {
            setTitleMsg(e.target.value);
          }}
        />
        <Form.Label htmlFor="destination">Destinatários</Form.Label>
        <Form.Control
          id="destination"
          type="text"
          className="mb-2"
          value={destinationMsg}
          placeholder="Use vírgula ao final do endereço, para informar mais de um destinatário. Para método de envio com WhatsApp, informe o número de telefone do contato no formato DDD + Número."
          onChange={(e) => {
            setDestinationMsg(e.target.value);
          }}
        />
        <Form.Label>Anexar arquivos</Form.Label>
        <SelectComponent
          isMulti
          options={fileOptions}
          name="files"
          onChange={setFilesMsg}
          className="mb-4"
          placeholder="Selecione os arquivos..."
        />
        {initialBody && <Editor onChange={setBodyMsg} text={initialBody} />}
      </Card.Body>
    </Card>
  );
}

LeadNotification.propTypes = {
  lead: PropTypes.shape({}).isRequired,
  stateInfo: PropTypes.bool.isRequired,
  stateAction: PropTypes.func.isRequired,
};
